// Shared helpers for the "blocked candidate" (v2) UI. The server serializer gives every
// applicant json `blocked` (bool) and `block` (null | { at, reason, by: {id, name},
// can_unblock, blocklist_id, cv_erasure_status, contact_suppression_status }).

export const BLOCKED_TOAST = 'המועמד/ת חסום/ה — הסרת חסימה ע"י מנהל/ת בלבד'

export function isApplicantBlocked (applicant) {
  return !!(applicant && applicant.blocked)
}

// dd/mm/yyyy without a moment dependency (the banner is used in places without $moment mocks).
export function formatBlockDate (iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const pad = n => (n < 10 ? '0' : '') + n
  return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear()
}

// Second banner line: what happened to the CV files.
export function cvStatusText (status) {
  switch (status) {
    case 'complete': return 'קורות החיים והתקצירים נמחקו'
    case 'pending': return 'מחיקת קו"ח: בתהליך'
    case 'failed': return 'מחיקת קו"ח: נכשלה'
    case 'not_requested': return 'קו"ח לא נמחקו (החלטת מנהל/ת)'
    default: return ''
  }
}

// Short labels for table cells (cv_erasure_status / contact_suppression_status).
export function cleanupStatusLabel (status) {
  switch (status) {
    case 'complete': return 'הושלם'
    case 'pending': return 'בתהליך'
    case 'failed': return 'נכשל'
    case 'not_requested': return 'לא נדרש'
    default: return '-'
  }
}

// Marks the applicant json as released in place (after a successful release call) so the
// parent's disabled controls and the banner refresh without reloading the applicant.
export function markReleased (vm, applicant) {
  if (!applicant) return
  vm.$set(applicant, 'blocked', false)
  vm.$set(applicant, 'block', null)
}

export function releaseErrorText (error) {
  const status = error && error.response && error.response.status
  const slug = error && error.response && error.response.data && error.response.data.error
  if (status === 403 || slug === 'manager_required') return 'רק מנהל/ת יכול/ה להסיר חסימה'
  if (slug === 'reason_required') return 'יש לציין סיבה (לפחות 3 תווים)'
  if (status === 404) return 'החסימה לא נמצאה - יש לרענן את העמוד'
  return 'הסרת החסימה נכשלה'
}

// Asks for a required free-text reason; returns the trimmed text, or null if cancelled/too short
// (a too-short answer toasts and returns null so the caller just stops).
export function askReason (promptText, emitToast) {
  const raw = window.prompt(promptText)
  if (raw === null || raw === undefined) return null
  const reason = String(raw).trim()
  if (reason.length < 3) {
    emitToast('יש לציין סיבה (לפחות 3 תווים)')
    return null
  }
  return reason.slice(0, 500)
}

export const BLOCK_CONFIRM_TEXT = 'קורות החיים, התקצירים והטקסטים שחולצו מקו"ח יימחקו לצמיתות. השיחות, ההגשות וההיסטוריה יישמרו. הגשה למעסיקים ויצירת קשר ייחסמו עד הסרת החסימה ע"י מנהל/ת.'

export function blockErrorText (error) {
  const slug = error && error.response && error.response.data && error.response.data.error
  if (slug === 'already_blocked') return 'המועמד/ת כבר חסום/ה'
  if (slug === 'applicant_not_found') return 'המועמד/ת לא נמצא/ה'
  if (slug === 'reason_required') return 'יש לציין סיבה (לפחות 3 תווים)'
  return 'חסימת המועמד/ת נכשלה'
}

// Block flow shared by the applicant form and the applicant modal: reason prompt (required)
// -> confirm -> POST. Resolves with the server response ({ blocked_candidate, applicant }),
// or null when cancelled / failed (a toast has already been shown).
export async function blockApplicantFlow (vm, applicantId) {
  const toast = (text, type) => vm.$utils.emit('show_toast', { text, type })
  const reason = askReason('סיבת החסימה (חובה):', t => toast(t, 'error'))
  if (!reason) return null
  if (!window.confirm(BLOCK_CONFIRM_TEXT)) return null
  try {
    const res = await vm.$network_utils.blockCandidate({ applicant_id: applicantId, reason })
    toast('המועמד/ת נחסם/ה. מחיקת קו"ח מתבצעת ברקע')
    return res
  } catch (error) {
    toast(blockErrorText(error), 'error')
    return null
  }
}
